import { AvailableCredit, FilteredCreditApplication, InputText, Merchant, StepStateUpdater } from '@applyonline/shared'
import { formatCurrency } from '@if/core/utils'
import { useCallback, useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import PromoCodeInput from '../../../components/PromoCodeInput'
import { Constants } from '../../../types'
import { WorksheetForm } from '../schema-hooks'

interface Props {
  maxAmountFinanced: number
  setStepState: StepStateUpdater
  creditApp: FilteredCreditApplication
  codePromo: string | null
  setCodePromo: (codePromo: string | null) => void
  merchant: Merchant
  availableCredit: AvailableCredit
}

const FinalAmountStep = ({
  maxAmountFinanced,
  setStepState,
  creditApp,
  merchant,
  setCodePromo,
  codePromo,
  availableCredit,
}: Props) => {
  const { t } = useTranslation()
  const { control, setValue, getValues } = useFormContext<WorksheetForm>()
  const {
    field,
    fieldState: { error, invalid },
  } = useController({
    name: 'amountRequested',
    control,
  })

  const [promoCodeIsValidating, setPromoCodeValidating] = useState<boolean>(false)
  const handleOnPaymentPlanIdChange = useCallback(
    (paymentPlanId: string | null, code: string | null) => {
      setValue('merchantPaymentPlanId', paymentPlanId, { shouldValidate: true, shouldDirty: true })
      setCodePromo(code)
    },
    [setCodePromo, setValue],
  )
  
  const canAddPromoCode = merchant.supportsPromotions

  useEffect(
    () => setStepState({ invalid, loading: promoCodeIsValidating }),
    [invalid, setStepState, promoCodeIsValidating],
  )

  useEffect(() => {
    if (field.value < Constants.LargeLoanAmount && getValues('term') === Constants.LongTerm)
      setValue('term', creditApp?.finalDecision?.maxTermDuration)
  }, [creditApp?.finalDecision?.maxTermDuration, field.value, getValues, setValue])

  if (availableCredit.availableAmount < Constants.MinLoanAmount)
    return (
      <h4>
        <Trans
          i18nKey="worksheet.finalAmount.amountUnavailableMessage"
          values={{ applicantName: creditApp.applicant.firstName + ' ' + creditApp.applicant.lastName }}
        />
      </h4>
    )

  return (
    <>
      <h3 style={{ marginBottom: '16px' }}>
        <i className="fa-solid fa-party-horn" />{' '}
        <Trans
          i18nKey="worksheet.finalAmount.title"
          values={{
            maxLoanAmount: formatCurrency(maxAmountFinanced),
          }}
        />
      </h3>
      {availableCredit.creditInProgress !== 0 && (
        <>
          <h4>
            <Trans
              i18nKey="worksheet.finalAmount.activeCredit"
              values={{ creditInProgress: formatCurrency(availableCredit.creditInProgress) }}
            />
          </h4>
          <h4 style={{ marginBottom: '16px' }}>
            <strong style={{ color: 'var(--accent-blue)' }}> {formatCurrency(availableCredit.availableAmount)} </strong>
            <Trans i18nKey="worksheet.finalAmount.availableCredit" />
          </h4>
        </>
      )}
      <h4 style={{ marginBottom: '16px' }}>{t('worksheet.finalAmount.confirmation')}</h4>
      <p style={{ marginBottom: '16px' }}>{t('worksheet.finalAmount.confirmation_subtext')}</p>
      <InputText error={error} label={t('worksheet.finalAmount.label')} id="amountRequested" type="number" {...field} />
      {canAddPromoCode && (
        <PromoCodeInput
          onChange={handleOnPaymentPlanIdChange}
          codePromo={codePromo}
          merchantId={merchant.id}
          setValidating={setPromoCodeValidating}
        />
      )}
    </>
  )
}

export default FinalAmountStep
